import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const GlobalFilters = ['timeFrame', 'environment'] as const;

type GlobalFilterKey = (typeof GlobalFilters)[number];

export function isGlobalFilterKey(key: string): key is GlobalFilterKey {
  return GlobalFilters.includes(key as GlobalFilterKey);
}

export const useGlobalFilter = (
  name: GlobalFilterKey,
  defaultValue?: string,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParam = searchParams.get(name) ?? defaultValue ?? null;

  const setFilter = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          prev.set(name, value);
          return prev;
        },
        { replace: false },
      );
    },
    [name, setSearchParams],
  );

  return [searchParam, setFilter] as const;
};

export const useGlobalQueryString = (): string => {
  const [searchParams] = useSearchParams();

  const filters = useMemo(() => {
    return GlobalFilters.filter((filter) => searchParams.get(filter))
      .map((filter) => {
        return `${filter}=${searchParams.get(filter)}`;
      })
      .join('&');
  }, [searchParams]);

  return filters;
};
